import { useEffect, useId, useState } from 'react'
import Icon, { CheckmarkIcon, SearchIcon } from '@design-systems/react-icons'

const LookupField = ({
  errorMessage,
  getInputValue,
  getOptionKey,
  id,
  initialQuery = '',
  isDisabled = false,
  isRequired = false,
  label,
  minimumQueryLength = 3,
  noResultsMessage,
  onQueryChange = () => {},
  onSelect,
  placeholder,
  renderOption,
  renderSelection,
  search,
  searchingMessage,
  value = null,
}) => {
  const generatedId = useId()
  const inputId = id || generatedId
  const listboxId = `${inputId}-options`
  const [query, setQuery] = useState(
    value ? getInputValue(value) : initialQuery,
  )
  const [options, setOptions] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    setQuery(value ? getInputValue(value) : initialQuery)
  }, [getInputValue, initialQuery, value])

  useEffect(() => {
    const normalizedQuery = query.trim()
    if (value || normalizedQuery.length < minimumQueryLength) {
      setOptions([])
      setIsLoading(false)
      setError('')
      return undefined
    }

    let isCurrent = true
    setIsLoading(true)
    setError('')
    const timer = window.setTimeout(async () => {
      try {
        const results = await search(normalizedQuery)
        if (isCurrent) setOptions(results)
      } catch {
        if (isCurrent) {
          setOptions([])
          setError(errorMessage)
        }
      } finally {
        if (isCurrent) setIsLoading(false)
      }
    }, 300)

    return () => {
      isCurrent = false
      window.clearTimeout(timer)
    }
  }, [errorMessage, minimumQueryLength, query, search, value])

  const handleQueryChange = ({ target: { value: nextQuery } }) => {
    setQuery(nextQuery)
    onQueryChange(nextQuery)
    if (value) onSelect(null)
  }

  const handleSelect = (option) => {
    const nextQuery = getInputValue(option)
    onSelect(option)
    setQuery(nextQuery)
    setOptions([])
    setError('')
  }

  const showNoResults =
    !value &&
    !isLoading &&
    !error &&
    query.trim().length >= minimumQueryLength &&
    options.length === 0

  return (
    <div className="lookup-field">
      <label htmlFor={inputId}>
        {label}
        {isRequired && <span aria-hidden="true">*</span>}
      </label>
      <div className="lookup-field__input-wrap">
        <Icon aria-hidden="true" size="sm" svg={SearchIcon} />
        <input
          aria-autocomplete="list"
          aria-controls={listboxId}
          aria-expanded={options.length > 0}
          autoComplete="off"
          id={inputId}
          onChange={handleQueryChange}
          placeholder={placeholder}
          disabled={isDisabled}
          role="combobox"
          type="text"
          value={query}
        />
      </div>
      {!value && query.trim().length < minimumQueryLength && (
        <small>Enter at least {minimumQueryLength} characters to search.</small>
      )}
      {isLoading && <small role="status">{searchingMessage}</small>}
      {error && (
        <small className="lookup-field__error" role="alert">
          {error}
        </small>
      )}
      {showNoResults && <small>{noResultsMessage}</small>}
      {options.length > 0 && (
        <ul className="lookup-field__options" id={listboxId} role="listbox">
          {options.map((option) => (
            <li key={getOptionKey(option)} role="presentation">
              <button
                aria-selected="false"
                onClick={() => handleSelect(option)}
                role="option"
                type="button"
              >
                {renderOption(option)}
              </button>
            </li>
          ))}
        </ul>
      )}
      {value && (
        <div className="lookup-field__selection">
          <Icon aria-hidden="true" size="sm" svg={CheckmarkIcon} />
          {renderSelection(value)}
        </div>
      )}
    </div>
  )
}

export default LookupField
